// PATH: src/components/coverage/CoverageCellDrawer.tsx
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import { useCoverageApi, type CoverageItem, type OssatureChapterItem } from "../../api/coverage/coverage";
import { StatusPill, type PillTone } from "../ui/StatusPill";
import { errorMessage } from "../ui/feedback";
import { tokens } from "../../theme/theme";
import { ChapterBar, STATE_LOOK } from "./CoverageSummary";
import {
  cellChapters,
  cellLessons,
  chapterRank,
  chapterState,
  coverageTrackLabel,
  type ChapterState,
  type MatrixCell,
} from "./coverage";
import { formatLessonsReady, isIncomplete, type ChapterIssue } from "./chapterIssues";
import IssuePills from "./IssuePills";

const STATE: Record<ChapterState, { label: string; tone: PillTone; hint: string }> = {
  playable: { label: "Jouable", tone: "success", hint: "Actif, au moins un objectif a des faits." },
  no_objectives: { label: "Sans objectifs", tone: "error", hint: "Importé et actif, mais aucun objectif extrait : extraire les objectifs." },
  no_facts: { label: "Sans faits", tone: "warning", hint: "Importé et actif, mais aucun objectif n'a de faits : extraire les faits." },
  inactive: { label: "Inactif", tone: "warning", hint: "Importé mais masqué aux élèves." },
  extra: { label: "Hors ossature", tone: "info", hint: "Importé sans chapitre correspondant dans l'ossature." },
  todo: { label: "À importer", tone: "neutral", hint: "Prévu par l'ossature, pas encore importé." },
};

/* Un défaut déjà dit par la pastille d'état n'est pas répété à côté. */
const SAID_BY_STATE: Partial<Record<ChapterState, ChapterIssue>> = {
  no_objectives: "no_objectives",
  no_facts: "no_facts",
  inactive: "inactive",
};

interface Props {
  programId: string;
  cell: MatrixCell | null;
  /**
   * Chapitres du programme déjà chargés par la page : la case y lit les
   * siens. Absent (non chargés, ou en échec) : la case les demande.
   */
  programChapters?: OssatureChapterItem[] | null;
  subjectName: string;
  gradeName: string;
  onClose: () => void;
}

const chaptersLink = (programId: string, row: CoverageItem, documentId?: string | null, issue?: "any") => {
  const q = new URLSearchParams({ program: programId, grade: row.grade_id, subject: row.subject_id });
  q.set("track", row.track_id ?? "common");
  if (issue) q.set("issue", issue);
  if (documentId) q.set("doc", documentId);
  return `/chapters?${q.toString()}`;
};

/** Détail d'une case : ses filières, et la liste des chapitres avec leur état. */
export const CoverageCellDrawer: React.FC<Props> = ({ programId, cell, programChapters, subjectName, gradeName, onClose }) => {
  const { listOssatureChapters } = useCoverageApi();
  const [fetched, setFetched] = React.useState<OssatureChapterItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [unavailable, setUnavailable] = React.useState(false);

  const subjectId = cell?.subjectId;
  const gradeId = cell?.gradeId;

  const preloaded = React.useMemo(
    () =>
      programChapters && subjectId && gradeId
        ? programChapters.filter((c) => c.subject_id === subjectId && c.grade_id === gradeId)
        : null,
    [programChapters, subjectId, gradeId],
  );
  const chapters = preloaded ?? fetched;

  React.useEffect(() => {
    if (!subjectId || !gradeId || preloaded) return;
    let alive = true;
    setFetched(null);
    setError(null);
    setUnavailable(false);
    listOssatureChapters({ program_id: programId, subject_id: subjectId, grade_id: gradeId })
      .then(({ data }) => alive && setFetched(data?.items ?? []))
      .catch((e) => {
        if (!alive) return;
        // Route absente sur un service pas encore à jour : on garde les comptes.
        if (e?.response?.status === 404) setUnavailable(true);
        else setError(errorMessage(e, "Impossible de charger les chapitres."));
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, subjectId, gradeId, Boolean(preloaded)]);

  const lessons = cell ? cellLessons(cell) : { lessons: 0, ready: 0 };
  const counts = cell ? cellChapters(cell) : null;
  const extra = counts ? counts.imported - counts.ossature_imported : 0;

  const rows = React.useMemo(
    () =>
      [...(cell?.rows ?? [])].sort((a, b) =>
        a.track_id === b.track_id ? 0 : !a.track_id ? -1 : !b.track_id ? 1 : (a.track_code ?? "").localeCompare(b.track_code ?? ""),
      ),
    [cell],
  );

  return (
    <Drawer
      anchor="right"
      open={Boolean(cell)}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", sm: 560 }, bgcolor: "background.default" } }}
    >
      {cell && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 2, bgcolor: "background.paper", borderBottom: `1px solid ${tokens.border}` }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {gradeName}
                </Typography>
                <Typography variant="h3">{subjectName}</Typography>
              </Box>
              <IconButton onClick={onClose} aria-label="Fermer" size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={3} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              {[
                ["Prévus", counts?.ossature_chapters ?? 0],
                ["Importés", counts?.ossature_imported ?? 0],
                ["Jouables", counts?.ossature_playable ?? 0],
                ...(extra > 0 ? [["Hors ossature", extra]] : []),
                ...(lessons.lessons > 0 ? [["Leçons prêtes", `${lessons.ready}/${lessons.lessons}`]] : []),
              ].map(([l, v]) => (
                <Box key={l as string}>
                  <Typography variant="caption" color="text.secondary">
                    {l}
                  </Typography>
                  <Typography sx={{ fontSize: "1.25rem", fontWeight: 650 }}>{v}</Typography>
                </Box>
              ))}
            </Stack>
            <Box sx={{ mt: 1.5 }}>
              {counts && <ChapterBar counts={counts} />}
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {unavailable && (
              <Alert severity="info" sx={{ mb: 2 }}>
                La liste des chapitres d'une case arrive avec la prochaine version de tc_embed_be. En attendant,
                les chapitres importés sont consultables dans l'écran Chapitres.
              </Alert>
            )}

            <Stack spacing={2.5}>
              {rows.map((row) => {
                const list = chapters?.filter((c) => (c.track_id ?? null) === (row.track_id ?? null));
                const incomplete = list?.filter(isIncomplete).length ?? 0;
                return (
                  <Box key={row.track_id ?? "common"}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      useFlexGap
                      columnGap={1}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2">
                        {rows.length > 1 || row.track_id ? coverageTrackLabel(row) : "Chapitres"}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {row.ossature_chapters > 0
                            ? `${row.ossature_playable}/${row.ossature_chapters} jouables · ${row.ossature_imported} importés`
                            : `${row.imported} importés`}
                          {row.lessons ? ` · ${formatLessonsReady(row)}` : ""}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1, fontWeight: 650, color: STATE_LOOK[row.in_catalog ? row.state : "hors_catalogue"].fg }}
                        >
                          {STATE_LOOK[row.in_catalog ? row.state : "hors_catalogue"].label}
                        </Typography>
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {incomplete > 0 && (
                          <Button
                            size="small"
                            color="error"
                            component={RouterLink}
                            to={chaptersLink(programId, row, null, "any")}
                            endIcon={<ArrowForwardIcon fontSize="small" />}
                          >
                            Voir les chapitres à compléter ({incomplete})
                          </Button>
                        )}
                        <Button
                          size="small"
                          component={RouterLink}
                          to={chaptersLink(programId, row)}
                          endIcon={<ArrowForwardIcon fontSize="small" />}
                          disabled={row.imported === 0}
                        >
                          Chapitres
                        </Button>
                      </Stack>
                    </Stack>

                    <Box sx={{ bgcolor: "background.paper", border: `1px solid ${tokens.border}`, borderRadius: 2.5, overflow: "hidden" }}>
                      {!chapters && !unavailable && !error &&
                        [0, 1, 2].map((i) => (
                          <Box key={i} sx={{ px: 2, py: 1.25 }}>
                            <Skeleton width={`${70 - i * 12}%`} />
                          </Box>
                        ))}
                      {list && list.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                          {row.in_catalog && row.state === "ossature_manquante"
                            ? "Offerte au catalogue sans ossature : aucun chapitre prévu. L'ossature se publie depuis tc_docsourcing."
                            : "Aucun chapitre."}
                        </Typography>
                      )}
                      {list?.map((c, i) => {
                        const state = chapterState(c);
                        const st = STATE[state];
                        const said = SAID_BY_STATE[state];
                        const content = (
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.1, minWidth: 0 }}>
                            <Typography className="mono" color="text.secondary" sx={{ width: 58, flexShrink: 0, whiteSpace: "nowrap" }}>
                              {chapterRank(c)}
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={550} dir="auto" noWrap title={c.title}>
                                {c.title}
                              </Typography>
                              {c.document_id && (
                                <Typography variant="caption" color="text.secondary">
                                  {[
                                    `v${c.latest_version ?? "?"}`,
                                    formatLessonsReady(c),
                                    `${c.objectives_with_facts}/${c.objectives} objectifs avec faits`,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </Typography>
                              )}
                              {c.document_id && (
                                <Box sx={{ mt: 0.5, "&:empty": { display: "none" } }}>
                                  <IssuePills chapter={c} exclude={said ? [said] : []} />
                                </Box>
                              )}
                            </Box>
                            <Tooltip title={st.hint}>
                              <span>
                                <StatusPill tone={st.tone}>{st.label}</StatusPill>
                              </span>
                            </Tooltip>
                          </Stack>
                        );
                        return (
                          <React.Fragment key={c.chapter_ref}>
                            {i > 0 && <Divider />}
                            {c.document_id ? (
                              <Box
                                component={RouterLink}
                                to={chaptersLink(programId, row, c.document_id)}
                                sx={{ display: "block", color: "inherit", textDecoration: "none", "&:hover": { bgcolor: tokens.surfaceMuted } }}
                              >
                                {content}
                              </Box>
                            ) : (
                              content
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default CoverageCellDrawer;
